import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Peer from "peerjs";

/**
 * REMOTE 2P: pairing overlay + the wire between two players.
 *
 * Unity asks for pairing (remoteRequest via ReactBridge.jslib); this overlay
 * runs the PeerJS handshake (free public broker — swap for self-hosted or
 * Supabase signaling later without touching Unity). Once the DataChannel and
 * voice are up:
 *   - our pose packets ("poseData" events) stream to the rival,
 *   - the rival's packets are re-dispatched locally as "poseData2", which
 *     already feeds Unity's data2 stream — the game can't tell remote from
 *     shared-camera versus,
 *   - tiny sync messages (start/fin/rematch/bye) flow via "remoteMsg".
 * Voice rides the same connection as a WebRTC audio call.
 */

type Phase = "closed" | "pick" | "hosting" | "joining" | "connecting" | "connected" | "lost";

const CODE_CHARS = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
const makeCode = () =>
  Array.from({ length: 4 }, () => CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]).join("");
const peerId = (code: string) => `aots-race-${code}`;

const RemotePlay: React.FC = () => {
  const [phase, setPhase] = useState<Phase>("closed");
  const [code, setCode] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [status, setStatus] = useState("");
  const modeRef = useRef(0);   // 0 = race, 2 = battle — host's pick rules
  const peerRef = useRef<Peer | null>(null);
  const dcRef = useRef<any>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const localAudioRef = useRef<MediaStream | null>(null);
  const inMatchRef = useRef(false);

  const sendUnity = (method: string, arg: string) => {
    try {
      (window as any).unityInstance?.SendMessage("Manager", method, arg);
    } catch {}
  };

  const teardown = (lost: boolean) => {
    try { dcRef.current?.close(); } catch {}
    try { peerRef.current?.destroy(); } catch {}
    try { localAudioRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    dcRef.current = null;
    peerRef.current = null;
    localAudioRef.current = null;
    if (lost && inMatchRef.current) {
      // Mid-match drop: unwind the game, then say so
      sendUnity("RemoteMsg", "bye");
      setPhase("lost");
    } else if (!lost) {
      setPhase("closed");
    }
    inMatchRef.current = false;
  };

  const reconTries = useRef(0);
  const roomRef = useRef("");
  const roleHostRef = useRef(false);
  // Coming back from another app: the peer link died while suspended - dial
  // the same room again with a fresh retry budget (Joshua 2026-09-08)
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState !== "visible" || !inMatchRef.current || !roomRef.current) return;
      const dc = dcRef.current as { open?: boolean } | null;
      if (dc && dc.open) return;
      reconTries.current = 0;
      window.setTimeout(() => tryReconnect(roleHostRef.current), 300);
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pageshow", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("pageshow", onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mid-match drops (free broker + long sessions) get 3 quiet retries on
  // the SAME room code before we give up (Joshua 2026-09-01 battle drop)
  const tryReconnect = (isHost: boolean) => {
    if (!inMatchRef.current || !roomRef.current || reconTries.current >= 3) {
      teardown(true);
      return;
    }
    reconTries.current += 1;
    setPhase("connecting");
    setStatus(`connection lost — RECONNECTING (${reconTries.current}/3)…`);
    try { peerRef.current?.destroy(); } catch {}
    peerRef.current = null;
    window.setTimeout(() => {
      if (isHost) {
        const peer = new Peer(peerId(roomRef.current));
        peerRef.current = peer;
        peer.on("connection", (dc: any) => {
          dc.on("open", () => {
            reconTries.current = 0;
            setStatus("");
            setPhase("closed");
            wireChannel(dc, true);
          });
        });
        peer.on("error", () => tryReconnect(true));
      } else {
        const peer = new Peer();
        peerRef.current = peer;
        peer.on("open", () => {
          const dc = peer.connect(peerId(roomRef.current), { reliable: false });
          dc.on("open", () => {
            reconTries.current = 0;
            setStatus("");
            setPhase("closed");
            wireChannel(dc, false);
          });
          dc.on("error", () => tryReconnect(false));
        });
        peer.on("error", () => tryReconnect(false));
      }
    }, 1200 * reconTries.current);
  };

  // Everything below wires ONE DataChannel once it exists
  const wireChannel = (dc: any, isHost: boolean) => {
    dcRef.current = dc;
    roleHostRef.current = isHost;

    const onPose = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (dc.open) {
        try { dc.send({ t: "p", d }); } catch {}
      }
    };
    const onMsg = (e: Event) => {
      const m = (e as CustomEvent).detail?.m;
      if (dc.open && m) {
        try { dc.send({ t: "m", m }); } catch {}
      }
    };
    window.addEventListener("poseData", onPose);
    window.addEventListener("remoteMsg", onMsg);

    dc.on("data", (raw: any) => {
      if (!raw || typeof raw !== "object") return;
      if (raw.t === "p" && raw.d) {
        window.dispatchEvent(new CustomEvent("poseData2", { detail: raw.d }));
      } else if (raw.t === "m" && raw.m) {
        if (typeof raw.m === "string" && raw.m.startsWith("start")) {
          // "start" or "start:2" — the host's mode pick rules both clients
          const mode = raw.m.includes(":") ? raw.m.split(":")[1] : "0";
          inMatchRef.current = true;
          setPhase("closed");   // overlay down — the match is starting
          sendUnity("RemoteStart", "guest:" + mode);
        } else {
          if (raw.m === "bye") inMatchRef.current = false;
          sendUnity("RemoteMsg", raw.m);
          if (raw.m === "bye") teardown(false);
        }
      }
    });
    dc.on("close", () => {
      window.removeEventListener("poseData", onPose);
      window.removeEventListener("remoteMsg", onMsg);
      tryReconnect(isHost);
    });
    dc.on("error", () => tryReconnect(isHost));

    if (isHost) {
      // Host fires the starting gun for both sides — its mode pick rules
      inMatchRef.current = true;
      setPhase("closed");   // overlay down — the match is starting
      try { dc.send({ t: "m", m: "start:" + modeRef.current }); } catch {}
      sendUnity("RemoteStart", "host:" + modeRef.current);
    }
  };

  const startVoice = async (peer: Peer, otherId: string | null) => {
    // Voice is best-effort: a denied mic never blocks the race
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      localAudioRef.current = stream;
      if (otherId) {
        const call = peer.call(otherId, stream);
        call?.on("stream", (remote: MediaStream) => {
          if (audioElRef.current) audioElRef.current.srcObject = remote;
        });
      }
      peer.on("call", (call: any) => {
        call.answer(stream);
        call.on("stream", (remote: MediaStream) => {
          if (audioElRef.current) audioElRef.current.srcObject = remote;
        });
      });
    } catch {
      setStatus("mic off — racing without voice");
    }
  };

  const host = () => {
    const c = makeCode();
    setCode(c);
    roomRef.current = c;
    setPhase("hosting");
    setStatus("waiting for your rival…");
    const peer = new Peer(peerId(c));
    peerRef.current = peer;
    peer.on("open", () => startVoice(peer, null));
    peer.on("connection", (dc: any) => {
      setStatus("rival found — connecting voice…");
      dc.on("open", () => wireChannel(dc, true));
    });
    peer.on("error", (err: any) => {
      if (err?.type === "unavailable-id") host(); // code collision: reroll
      else { setStatus("connection error: " + (err?.type || "unknown")); }
    });
  };

  const join = (codeArg?: string) => {
    const c = (typeof codeArg === "string" ? codeArg : joinCode).trim().toUpperCase();
    if (c.length !== 4) { setStatus("codes are 4 letters"); return; }
    roomRef.current = c;
    setPhase("connecting");
    setStatus("finding room " + c + "…");
    const peer = new Peer();
    peerRef.current = peer;
    peer.on("open", () => {
      const dc = peer.connect(peerId(c), { reliable: false });
      dc.on("open", () => {
        setStatus("connected — waiting for start…");
        startVoice(peer, peerId(c));
        wireChannel(dc, false);
      });
      dc.on("error", () => setStatus("couldn't reach that room"));
    });
    peer.on("error", (err: any) => {
      if (err?.type === "peer-unavailable") setStatus("no room " + c + " — check the code");
      else setStatus("connection error: " + (err?.type || "unknown"));
    });
  };

  // QA harness (?qa=1 only): scripted hosting for the two-agent remote rig
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("qa")) return;
    const onHost = (e: Event) => {
      modeRef.current = ((e as CustomEvent).detail?.mode ?? 2) | 0;
      setPhase("pick");
      host();
    };
    window.addEventListener("qaHostRoom", onHost);
    return () => window.removeEventListener("qaHostRoom", onHost);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Room link (?join=CODE): once the player taps Start, join automatically
  useEffect(() => {
    const c = new URLSearchParams(window.location.search).get("join");
    if (!c || c.length !== 4) return;
    const onStarted = () => {
      modeRef.current = -1;
      setJoinCode(c.toUpperCase());
      join(c);
    };
    window.addEventListener("gameStarted", onStarted, { once: true });
    return () => window.removeEventListener("gameStarted", onStarted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    const onRequest = (e: Event) => {
      modeRef.current = (e as CustomEvent).detail?.mode | 0;
      setPhase("pick");
      setStatus("");
      setJoinCode("");
    };
    window.addEventListener("remoteRequest", onRequest);
    return () => window.removeEventListener("remoteRequest", onRequest);
  }, []);

  const btn: React.CSSProperties = {
    display: "block", width: "100%", padding: "14px 0", margin: "10px 0",
    fontSize: 17, fontWeight: 700, borderRadius: 8, border: "none",
    cursor: "pointer", background: "#16a3b8", color: "#fff",
  };

  // The <audio> element must be STABLE across overlay open/close, or the
  // remote voice stream detaches when the race starts and the UI unmounts
  const overlay = phase === "closed" ? null : createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.92)",
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", color: "#fff", fontFamily: "sans-serif",
    }}>
      <div style={{ width: "min(85vw, 340px)", textAlign: "center" }}>
        <div style={{ fontSize: 26, fontWeight: 800, marginBottom: 6 }}>
          {modeRef.current === -1 ? "JOIN A FRIEND"
            : modeRef.current === 2 ? "REMOTE BATTLE" : "REMOTE RACE"}
        </div>

        {phase === "pick" && (
          <>
            <div style={{ opacity: 0.75, fontSize: 14, marginBottom: 14 }}>
              {modeRef.current === -1
                ? "Type the 4-letter code your friend reads out. Their mode pick rules the match."
                : "One of you creates the room and reads the code out; the other joins. Voice chat connects automatically."}
            </div>
            {modeRef.current !== -1 && <button style={btn} onClick={host}>CREATE ROOM</button>}
            <input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="CODE"
              maxLength={4}
              style={{
                width: "100%", padding: "12px 0", fontSize: 22, textAlign: "center",
                letterSpacing: 8, borderRadius: 8, border: "1px solid #555",
                background: "#111", color: "#fff", margin: "6px 0", boxSizing: "border-box",
              }}
            />
            <button style={{ ...btn, background: "#2ba84a" }} onClick={join}>JOIN ROOM</button>
          </>
        )}

        {(phase === "hosting" || phase === "connecting") && (
          <>
            {phase === "hosting" && (
              <>
                <div style={{ fontSize: 46, fontWeight: 800, letterSpacing: 12, margin: "12px 0" }}>{code}</div>
                <button
                  style={{ ...btn, background: linkCopied ? "#2ba84a" : "#444" }}
                  onClick={() => {
                    const link = window.location.origin + "/?join=" + code;
                    navigator.clipboard?.writeText(link)
                      .then(() => { setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2500); })
                      .catch(() => window.prompt("Copy this link:", link));
                  }}
                >
                  {linkCopied ? "LINK COPIED ✓" : "COPY INVITE LINK"}
                </button>
                <div style={{ opacity: 0.6, fontSize: 12, marginTop: 2 }}>
                  Send it - they click, tap Start, and land in your room.
                </div>
              </>
            )}
            <div style={{ opacity: 0.8, fontSize: 15 }}>{status}</div>
          </>
        )}

        {phase === "connected" && (
          <div style={{ opacity: 0.85, fontSize: 16 }}>Connected — get to the line!</div>
        )}

        {phase === "lost" && (
          <>
            <div style={{ fontSize: 18, marginBottom: 10 }}>Your rival disconnected.</div>
            <button style={btn} onClick={() => setPhase("closed")}>OK</button>
          </>
        )}

        {phase !== "lost" && phase !== "connected" && (
          <button
            style={{ ...btn, background: "#555", marginTop: 18 }}
            onClick={() => teardown(false)}
          >
            CANCEL
          </button>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <audio ref={audioElRef} autoPlay />
      {overlay}
    </>
  );
};

export default RemotePlay;
